import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { FileText, ShieldCheck, Bell, Layers } from 'lucide-react';
import { buildAdminUrl } from '@/lib/workspace';

const PublicUploadPage = lazy(() =>
  import('@/components/upload/PublicUploadPage').then((m) => ({ default: m.PublicUploadPage })),
);
const PublicSharePage = lazy(() =>
  import('@/components/share/PublicSharePage').then((m) => ({ default: m.PublicSharePage })),
);

function LazyFallback() {
  return (
    <div className="flex items-center justify-center h-32">
      <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

function MarketingLanding() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-7 w-7 text-primary" />
            <span className="text-lg font-semibold">iCrop</span>
          </div>
          <Button asChild>
            <a href={buildAdminUrl('/login')}>Sign in</a>
          </Button>
        </div>
      </header>

      <section className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
          Document compliance for the food supply chain
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
          iCrop centralizes certifications, audits, and FSVP compliance for importers,
          exporters, and processors. One platform per client, isolated data, your own branding.
        </p>
        <Button asChild size="lg">
          <a href={buildAdminUrl('/login')}>Sign in</a>
        </Button>
      </section>

      <section className="container mx-auto px-4 pb-20">
        <div className="grid gap-6 md:grid-cols-3">
          <Card>
            <CardHeader>
              <ShieldCheck className="h-8 w-8 text-primary mb-2" />
              <CardTitle>FSVP Compliance</CardTitle>
              <CardDescription>
                Manage certificates, expiration dates, and traceability without spreadsheets.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <Bell className="h-8 w-8 text-primary mb-2" />
              <CardTitle>Expiration alerts</CardTitle>
              <CardDescription>
                Automatic notifications 60, 30, 15, and 7 days before any key document
                expires.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <Layers className="h-8 w-8 text-primary mb-2" />
              <CardTitle>Multi-organization</CardTitle>
              <CardDescription>
                Group farms, plants, and suppliers by client. A dedicated workspace per brand.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      <footer className="border-t">
        <div className="container mx-auto px-4 py-6 text-sm text-muted-foreground flex justify-between">
          <span>© {new Date().getFullYear()} iCrop</span>
          <Link to="/" className="hover:text-primary">
            iCrop.net
          </Link>
        </div>
      </footer>
    </div>
  );
}

const marketingRouter = createBrowserRouter([
  {
    path: '/',
    element: <MarketingLanding />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/upload/:token',
    element: (
      <Suspense fallback={<LazyFallback />}>
        <PublicUploadPage />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/share/:token',
    element: (
      <Suspense fallback={<LazyFallback />}>
        <PublicSharePage />
      </Suspense>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '*',
    element: <MarketingLanding />,
  },
]);

export function MarketingRoutes() {
  return <RouterProvider router={marketingRouter} />;
}
